/**
 * Quem somos photo gallery.
 *
 * A lightweight carousel where the main image and thumbnails exchange places.
 * Clicking a thumbnail promotes it to the main stage and moves the previous
 * main image into that exact thumbnail slot.
 *
 * Markup contract (see template-parts/sections/quem-somos/gallery.php):
 * - [data-gallery]            section wrapper
 * - [data-gallery-main]       <img> shown on the main stage (index 0)
 * - [data-gallery-prev/next]  arrow buttons
 * - [data-gallery-thumb]      thumbnail buttons carrying data-full/alt
 *
 * @param {Document} root
 */
export function initQuemSomosGallery(root = document) {
  const gallery = root.querySelector('[data-gallery]');

  if (!gallery || gallery.dataset.galleryInitialized === 'true') {
    return;
  }

  const main = gallery.querySelector('[data-gallery-main]');
  const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));

  if (!main || thumbs.length === 0) {
    return;
  }

  gallery.dataset.galleryInitialized = 'true';

  // Display order: position 0 is the large image; the other positions are thumbs.
  const displayedSlides = [
    { full: main.dataset.full || main.getAttribute('src'), alt: main.dataset.alt || main.alt },
    ...thumbs.map((thumb) => ({ full: thumb.dataset.full, alt: thumb.dataset.alt || '' })),
  ];

  const render = (activeThumbPosition = -1) => {
    const slide = displayedSlides[0];
    main.src = slide.full;
    main.alt = slide.alt;
    main.dataset.full = slide.full;
    main.dataset.alt = slide.alt;

    thumbs.forEach((thumb, index) => {
      const thumbPosition = index + 1;
      const thumbSlide = displayedSlides[thumbPosition];
      const thumbImage = thumb.querySelector('img');
      const active = thumbPosition === activeThumbPosition;

      thumb.dataset.full = thumbSlide.full;
      thumb.dataset.alt = thumbSlide.alt;
      thumb.dataset.position = String(thumbPosition);
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-label', `Ver foto ${thumbPosition + 1}`);

      if (thumbImage) {
        thumbImage.src = thumbSlide.full;
        thumbImage.alt = '';
      }

      if (active) {
        thumb.setAttribute('aria-current', 'true');
        return;
      }

      thumb.removeAttribute('aria-current');
    });
  };

  const swapWithThumb = (position) => {
    if (!displayedSlides[position]) {
      return;
    }

    [displayedSlides[0], displayedSlides[position]] = [
      displayedSlides[position],
      displayedSlides[0],
    ];

    render(position);
  };

  const showNext = () => {
    const currentMain = displayedSlides.shift();

    displayedSlides.push(currentMain);
    render(displayedSlides.length - 1);
  };

  const showPrev = () => {
    const nextMain = displayedSlides.pop();

    displayedSlides.unshift(nextMain);
    render(1);
  };

  const prev = gallery.querySelector('[data-gallery-prev]');
  const next = gallery.querySelector('[data-gallery-next]');

  render();
  next?.classList.add('is-active');

  prev?.addEventListener('click', () => {
    prev.classList.add('is-active');
    next?.classList.remove('is-active');
    showPrev();
  });

  next?.addEventListener('click', () => {
    next.classList.add('is-active');
    prev?.classList.remove('is-active');
    showNext();
  });

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => swapWithThumb(Number(thumb.dataset.position)));
  });
}
